import { PostMessage, getApp } from './common'

/**
 * 统一调用 APP 接口方法
 */
export default class WebviewInvokeApp extends PostMessage {
  constructor(opt) {
    super(opt)
  }

  /**
   * 注册接口必须使用 _register 注册，否则调用时报错
   * 该文件下仅可写对外使用的接口，公共属性请写到 common.js 的 PostMessage 中
   * _register: 第一项为注册的接口方法，第二项为配置，配置 rules 可做版本校验，详情请看 ./proxy.js
   */

  // 获取当前APP平台
  getApp = getApp

  /**
   * 进入 APP 登录页面
   */
  enterLoginPage = this._register(() => {
    this._communityInvokeApp({
      action: 'enterLoginPage',
      data: {},
    })
  })

  /**
   * 调用APP分享功能
   * @param {string} url 分享的地址
   * @param {string} title 分享的标题
   * @param {string} coverUrl 分享的封面
   */
  showShareAlert = this._register(({ url, title, coverUrl }) => {
    this._communityInvokeApp({
      action: 'showShareAlert',
      data: { url, title, coverUrl },
    })
  })

  /**
   * 跳转到APP用户主页
   * @param {number} userId 用户ID
   */
  showUserProfile = this._register((userId) => {
    this._communityInvokeApp({
      action: 'showUserProfile',
      data: { userId },
    })
  })

  /**
   * 跳转到APP单个Post详情页
   * @param {string} postId POST ID
   */
  showSinglePost = this._register((postId) => {
    this._communityInvokeApp({
      action: 'showSinglePost',
      data: { postId },
    })
  })

  /**
   * 跳转到APP的每周精选Post列表
   * @param {string} periodId 每周精选期数ID
   * @param {string} postId POST ID
   */
  showPostList = this._register(
    (periodId, postId) => {
      this._communityInvokeApp({
        action: 'showPostList',
        data: { periodId, postId },
      })
    },
    {
      rules: [
        { version: '1.8.0', app: 'ios' },
        { version: '1.8.0', app: 'android' },
      ],
    }
  )

  /**
   * 跳转到Tag分享页主页
   * @param {string} tag Tag名
   */
  showTagHome = this._register((tag) => {
    this._communityInvokeApp({
      action: 'showTagHome',
      data: { tag },
    })
  })

  /**
   * 传递横向滚动区域的位置和大小
   * @param {number} x 横向滚动区域的x轴位置
   * @param {number} y 横向滚动区域的y轴位置
   * @param {number} width 横向滚动区域的宽度
   * @param {number} height 横向滚动区域的高度
   */
  postBannerInfo = this._register((x, y, width, height) => {
    this._communityInvokeApp({
      action: 'postBannerInfo',
      data: { x, y, width, height },
    })
  })

  /**
   * 调用App播放器
   * @param {object} post Post完整数据
   */
  launchPlayer = this._register((post) => {
    this._communityInvokeApp({
      action: 'launchPlayer',
      data: { post },
    })
  })

  /**
   * 调用App帖子评论列表
   * @param {string} postId PostID
   */
  launchCommont = this._register((postId) => {
    this._communityInvokeApp({
      action: 'launchComment',
      data: { postId },
    })
  })

  /**
   * 调用App打开第三方App或浏览器
   * @param {string} platform 平台: tb|jd|web
   * @param {string} url 链接
   */
  showBuyApp = this._register((platform, url) => {
    this._communityInvokeApp({
      action: 'showBuyApp',
      data: { platform, url },
    })
  })

  /**
   * 调用App打开新的webview窗口
   * @param {string} url 链接
   */
  showWebpage = this._register(
    (url) => {
      if (!url) return false
      if (url.indexOf('/') === 0) {
        url = window.location.origin + url
      }
      this._communityInvokeApp({
        action: 'showWebpage',
        data: { url },
      })
        .then((success) => {
          if (!success) window.open(url)
        })
        .catch((err) => {
          window.open(url)
        })
    },
    {
      rules: [
        // IOS不确定上线的版本
        { app: 'ios' },
        // 安卓1.16.0仅活动页支持
        { version: '1.6.0', app: 'android' },
      ],
    }
  )

  /**
   * 调用App打开原生APP页面
   * @param {string} url 链接
   * url:
   *  TrickHome（本地玩法页）
   *  FeatureHome（本地发现页）
   *  ShotLabHome（本地故事页）
   *  PublishPost（本地发帖页）
   *  UserSetting（用户设置页）(> 1.6.0)
   */
  jumpPage = this._register(
    (url, creatorApplyNo) => {
      const params = { url }
      if (creatorApplyNo && url === 'PublishPost') {
          params.creatorApplyNo = creatorApplyNo
      }
      this._communityInvokeApp({
        action: 'jumpPage',
        data: params,
      })
    },
    {
      rules: (url) => {
        let rules = [{ version: '1.5.0' }]
        switch (url) {
          case 'UserSetting':
            rules = [{ version: '1.6.0' }]
        }
        return rules
      },
    }
  )

  /**
   * 进行任务（webview）
   */
  openWebView = this._register(({ taskType, url, fullscreen }) => {
    this._communityInvokeApp({
      action: 'openWebView',
      data: {
        url,
        closable: taskType ? false : true, // 选填
        taskType, // 选填
        openId: taskType && openIdMap[taskType], // 选填
        fullscreen, // 选填
      },
    })
  })

  /**
   * 调用App执行原生返回操作
   */
  goBack = this._register(
    () => {
      this._communityInvokeApp({
        action: 'goBack',
        data: {},
      })
    },
    {
      rules: [{ version: '1.6.0' }],
    }
  )

  /**
   * 调用APP执行顶栏配置更新
   * @param {boolean} hideTopBar 是否隐藏顶栏
   */
  updateTopBar = this._register(
    async ({ hideTopBar }) => {
      await this._communityInvokeApp({
        action: 'updateTopBar',
        data: { hideTopBar },
      })
    },
    {
      rules: [{ version: '1.6.0' }],
    }
  )
  // Tip:
  // 因为_communityInvokeApp设计时action必须和函数名一致
  // 所以将原hideTopBar改为updateTopBar
  // 并在此补充一个hideTopBar转发到updateTopBar上
  /**
   * 调用APP执行隐藏顶栏
   * @param {boolean} hideTopBar 是否隐藏顶栏
   */
  hideTopBar = this._register(
    async (hideTopBar) => {
      await this.updateTopBar({ hideTopBar })
    },
    {
      rules: [{ version: '1.6.0' }],
    }
  )

  /**
   * APP调用滑块验证
   */
  getSlideSignInfo(result) {
    if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.getSlideData) {
      // WKWebView
      window.webkit.messageHandlers.getSlideData.postMessage(JSON.stringify(result))
    } else if (window.slideInterface) {
      // Android getSlideData
      window.slideInterface.getSlideData(JSON.stringify(result))
    }
  }

  /**
   * 调用APP发帖
   * @param {*} tag
   */
  postShareTag(tag) {
    let app = getApp()
    if (app === 'ios') {
      window.webkit.messageHandlers.communityContributeWeb.postMessage(JSON.stringify({ tag }))
    } else if (app === 'android') {
      window.communityContributeWeb.postShareTag(JSON.stringify({ tag }))
    }
  }
}
